import React, { useState, useEffect, useContext, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { format, differenceInMinutes } from 'date-fns';
import {
  Clock, User, MapPin, Activity, Eye, EyeOff,
  LogOut, Settings, Plus, ChevronRight,
  AlertCircle, Check, Home, Users, Stethoscope, X, XCircle, Edit2
} from 'lucide-react';
import { AuthContext } from '../contexts/AuthContext';
import { supabase } from '../services/supabase';
import { subscribeAppointments } from '../lib/realtimeTransport';
import api from '../services/api';
import './Dashboard.css';
import DurationModal from '../components/DurationModal';
import SiteSelector from '../components/SiteSelector';
import NotificationToggle from '../components/NotificationToggle';
import { ThemeToggle } from '../theme/ThemeToggle';
import OneSignal from 'react-onesignal';

interface Investigator {
  id: string;
  name: string;
  display_order: number;
  is_active: boolean;
}


interface Appointment {
  id: string;
  appointment_date: string;
  appointment_time: string;
  patient_initials: string;
  subject_id?: string;
  study_name: string;
  visit_name: string;
  crc_name: string;
  patient_status: string;
  room_id: string | null;
  rooms?: { room_name: string };
  check_in_time: string | null;
  check_out_time: string | null;
  current_status_start: string | null;
  is_hidden: boolean;
  assigned_user_id: string | null;
  needs_return_to_crc?: boolean;
  investigator_name?: string | null;
  notes?: string;
  manual_duration_override?: number;
  manual_duration_note?: string;
}

interface Room {
  id: string;
  room_name: string;
  is_active: boolean;
}

interface ColorThresholds {
  green: number;
  yellow: number;
  red: number;
}

interface Filters {
  status: string[];
  study: string[];
  crc: string[];
  investigator: string[];
  hideCheckedOut: boolean;
  showOnlyOnsite: boolean;
}

const Dashboard: React.FC = () => {
  const { user, currentSite, isAdmin } = useContext(AuthContext);
  const navigate = useNavigate();

  // Entitlement check state
  const [hasEntitlement, setHasEntitlement] = useState(false);
  const [checkingEntitlement, setCheckingEntitlement] = useState(true);
  const [entitlementChecked, setEntitlementChecked] = useState(false);

  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [rooms, setRooms] = useState<Room[]>([]);
  const [investigators, setInvestigators] = useState<Investigator[]>([]);
  const [filters, setFilters] = useState<Filters>(() => {
    // Restore filters from localStorage if available
    const saved = localStorage.getItem('statusBoardFilters');
    if (saved) {
      try {
        return JSON.parse(saved) as Filters;
      } catch {
        // Invalid JSON, use defaults
      }
    }
    return {
      status: [],
      study: [],
      crc: [],
      investigator: [],
      hideCheckedOut: false,
      showOnlyOnsite: false
    };
  });
  const [colorThresholds, setColorThresholds] = useState<ColorThresholds>({
    green: 15,
    yellow: 30,
    red: 45
  });
  const [showRoomDropdown, setShowRoomDropdown] = useState<string | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [newAppointment, setNewAppointment] = useState({
    patient_initials: '',
    subject_id: '',
    study_name: '',
    visit_name: '',
    crc_name: '',
    appointment_time: format(new Date(), 'HH:mm')
  });
  const [showStatusDropdown, setShowStatusDropdown] = useState(false);
  const [showStudyDropdown, setShowStudyDropdown] = useState(false);
  const [showCRCDropdown, setShowCRCDropdown] = useState(false);
  const [showInvestigatorDropdown, setShowInvestigatorDropdown] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [showStatusMenu, setShowStatusMenu] = useState<string | null>(null);
  const [showLocationMenu, setShowLocationMenu] = useState<string | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingAppointment, setEditingAppointment] = useState<Appointment | null>(null);
  const [showNotesModal, setShowNotesModal] = useState(false);
  const [editingNotes, setEditingNotes] = useState({ appointmentId: '', notes: '' });
  const [showDurationModal, setShowDurationModal] = useState<string | null>(null);
  const [selectedAppointmentForDuration, setSelectedAppointmentForDuration] = useState<Appointment | null>(null);
  const [notificationsEnabled, _setNotificationsEnabled] = useState(false);
  const [expandedMobileRows, setExpandedMobileRows] = useState<Set<string>>(new Set());
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const [headerHidden, setHeaderHidden] = useState(false);
  const lastScrollY = useRef(0);

  // Include no_show, cancelled, and rescheduled in status options
  const statusOptions = [
    { value: 'not_arrived', label: 'Not Arrived' },
    { value: 'lobby', label: 'Lobby' },
    { value: 'in_room', label: 'Ready CRC' },
    { value: 'with_crc', label: 'w/CRC' },
    { value: 'waiting_on_inv', label: 'Waiting on INV' },
    { value: 'with_investigator', label: 'w/Investigator' },
    { value: 'done', label: 'Done' },
    { value: 'no_show', label: 'No Show' },
    { value: 'cancelled', label: 'Cancelled' },
    { value: 'rescheduled', label: 'Rescheduled' }
  ];

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Check Status Board entitlement when component mounts or site changes
  useEffect(() => {
    const checkEntitlement = async () => {
      // Super admins always have access
      if (user?.role === 'super_admin') {
        setHasEntitlement(true);
        setEntitlementChecked(true);
        setCheckingEntitlement(false);
        return;
      }

      if (!currentSite) {
        // No site selected = no access to tools
        setHasEntitlement(false);
        setEntitlementChecked(true);
        setCheckingEntitlement(false);
        return;
      }

      try {
        const response = await api.get('/api/my-tools');
        const tools = response.data || [];
        const statusBoardTool = tools.find((t: any) => t.tool_id === 'status-board');
        setHasEntitlement(statusBoardTool?.is_available === true);
        setEntitlementChecked(true);
      } catch (error) {
        console.error('Error checking Status Board entitlement:', error);
        setHasEntitlement(false);
        setEntitlementChecked(true);
      } finally {
        setCheckingEntitlement(false);
      }
    };

    checkEntitlement();
  }, [user?.role, currentSite]);

 useEffect(() => {
    // Tag the user in OneSignal with their CRC name (first name only)
    if (user?.full_name) {
      const firstName = user.full_name.split(' ')[0];
      OneSignal.User.addTag("crc_name", firstName);
      
    }
  }, [user]);

  // Refetch data when site changes
  useEffect(() => {
    if (currentSite) {
      fetchAppointments();
      fetchRooms();
      fetchSettings();
      fetchInvestigators();
    }
  }, [currentSite?.id]);

  // Scroll-based header hiding for mobile
  useEffect(() => {
    if (!isMobile) {
      setHeaderHidden(false);
      return;
    }

    const handleScroll = () => {
      const currentScrollY = window.scrollY;

      // Show header when at or near top
      if (currentScrollY <= 10) {
        setHeaderHidden(false);
      }
      // Hide header when scrolling down
      else if (currentScrollY > lastScrollY.current && currentScrollY > 50) {
        setHeaderHidden(true);
      }

      lastScrollY.current = currentScrollY;
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isMobile]);

  useEffect(() => {
    if (!currentSite) return;

    // Set up real-time subscription with notification logic (site-scoped) through
    // the realtime transport adapter (WP-3.3). Under the default 'supabase'
    // transport this is byte-identical to the previous
    // supabase.channel(`appointments-${id}`).on('postgres_changes', ...); under
    // 'sse' the same { eventType, new, old } payloads arrive over the shared
    // EventSource, so the handler below is unchanged.
    const subscription = subscribeAppointments(currentSite.id,
        (payload) => {

          // Check if this is an UPDATE event and status changed to 'in_room' (Ready CRC)
          if (payload.eventType === 'UPDATE' && payload.new) {
            const updatedAppointment = payload.new as Appointment;
            
            


            // Only notify if:
            // 1. Status changed to 'in_room' (Ready CRC)
            // 2. The logged-in user is the assigned CRC (compare first names only)
            // 3. The previous status was different (not already in_room)
            const userFirstName = user?.full_name?.split(' ')[0] || '';
            const appointmentCRCFirstName = updatedAppointment.crc_name?.split(' ')[0] || '';
            
          


            if (updatedAppointment.patient_status === 'in_room' && 
                userFirstName === appointmentCRCFirstName) {
              
                   
              // Small delay to ensure room info is available
              setTimeout(async () => {
                // Fetch both appointments AND rooms to ensure we have current data
                await fetchAppointments();
                await fetchRooms();
                
                // Find the appointment in the updated list to get room info
                const apt = appointments.find(a => a.id === updatedAppointment.id);
                if (apt) {
                  showNotification(apt);
                } else {
                  // Fallback - fetch room name directly
                  const roomResponse = await api.get('/api/rooms');
                  const room = roomResponse.data.find((r: Room) => r.id === updatedAppointment.room_id);
                  const roomName = room?.room_name || 'a room';
                  
                  // Create notification with room name
                  new Notification('🔔 Patient Ready for CRC', {
                    body: `${updatedAppointment.patient_initials} (${updatedAppointment.study_name}) is ready in ${roomName}`,
                    icon: '/favicon.ico',
                    badge: '/favicon.ico',
                    tag: updatedAppointment.id,
                    requireInteraction: true,
                    silent: false
                  });
                }
              }, 500);
            }
          }
          
          // Always refresh the appointments list
          fetchAppointments();
        }
      );

    // Refresh every 30 seconds
    const interval = setInterval(() => {
      fetchAppointments();
    }, 30000);

    // Close dropdowns when clicking outside
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest('.room-dropdown-container')) {
        setShowRoomDropdown(null);
      }
      if (!target.closest('.filter-dropdown-container.status')) {
        setShowStatusDropdown(false);
      }
      if (!target.closest('.filter-dropdown-container.study')) {
        setShowStudyDropdown(false);
      }
      if (!target.closest('.filter-dropdown-container.crc')) {
        setShowCRCDropdown(false);
      }
      if (!target.closest('.filter-dropdown-container.investigator')) {
        setShowInvestigatorDropdown(false);
      }
      if (!target.closest('.status-menu-container')) {
        setShowStatusMenu(null);
      }
      if (!target.closest('.location-menu-container')) {
        setShowLocationMenu(null);
      }
    };

    document.addEventListener('click', handleClickOutside);

    return () => {
      subscription();
      clearInterval(interval);
      document.removeEventListener('click', handleClickOutside);
    };
  }, [currentSite?.id]);

  // Persist filters to localStorage when they change
  useEffect(() => {
    localStorage.setItem('statusBoardFilters', JSON.stringify(filters));
  }, [filters]);

  // Function to show desktop notification
  const showNotification = (appointment: Appointment) => {
    // Get room name - check both rooms object and find in rooms array
    let roomName = appointment.rooms?.room_name;
    if (!roomName && appointment.room_id) {
      const room = rooms.find(r => r.id === appointment.room_id);
      roomName = room?.room_name;
    }
    
    console.log('🔔 showNotification called!', {
      notificationsEnabled,
      hasNotificationAPI: 'Notification' in window,
      permission: Notification.permission,
      appointment: appointment.patient_initials,
      roomInfo: appointment.rooms?.room_name,
      roomId: appointment.room_id,
      foundRoomName: roomName
    });
    
    if (!('Notification' in window) || Notification.permission !== 'granted') {
      console.log('❌ Notifications blocked - not showing');
      return;
    }

    const title = '🔔 Patient Ready for CRC';
    const body = `${appointment.patient_initials} (${appointment.study_name}) is ready in ${roomName || 'a room'}`;
    const notification = new Notification(title, {
      body: body,
      icon: '/favicon.ico',
      badge: '/favicon.ico',
      tag: appointment.id,
      requireInteraction: true,
      silent: false
    });

    notification.onclick = () => {
      window.focus();
      notification.close();
    };
  };

  const fetchAppointments = async () => {
    if (!currentSite) return;
    try {
      const response = await api.get('/api/appointments');

      // Preserve existing investigator selections
      const currentInvestigatorSelections = new Map(
        appointments.map(apt => [apt.id, apt.investigator_name])
      );

      // Sort by appointment_time first, then by id for stable ordering
      const sortedData = response.data.map((apt: Appointment) => ({
        ...apt,
        // Preserve investigator selection if it exists locally but not in server response
        investigator_name: apt.investigator_name || currentInvestigatorSelections.get(apt.id) || null
      })).sort((a: Appointment, b: Appointment) => {
        // First sort by appointment_time
        if (a.appointment_time < b.appointment_time) return -1;
        if (a.appointment_time > b.appointment_time) return 1;
        // If times are equal, sort by id to maintain consistent order
        if (a.id < b.id) return -1;
        if (a.id > b.id) return 1;
        return 0;
      });

      setAppointments(sortedData);
    } catch (error) {
      console.error('Error fetching appointments:', error);
    }
  };

  const fetchRooms = async () => {
    if (!currentSite) return;
    try {
      const response = await api.get('/api/rooms');
      setRooms(response.data);
    } catch (error) {
      console.error('Error fetching rooms:', error);
    }
  };

  const fetchSettings = async () => {
    if (!currentSite) return;
    try {
      const response = await api.get('/api/settings');
      if (response.data.color_thresholds) {
        setColorThresholds(response.data.color_thresholds);
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
    }
  };

  const fetchInvestigators = async () => {
    if (!currentSite) return;
    try {
      const response = await api.get(`/api/sites/${currentSite.id}/investigators`);
      setInvestigators(response.data);
    } catch (error) {
      console.error('Error fetching investigators:', error);
    }
  };

  const updateAppointmentStatus = async (appointmentId: string, newStatus: string, roomId?: string | null, needsReturn?: boolean) => {
    try {
      // Clear room if status is changing to done, no_show, cancelled, or rescheduled
      const shouldClearRoom = newStatus === 'done' || newStatus === 'no_show' || newStatus === 'cancelled' || newStatus === 'rescheduled';
      
      // Handle room_id properly - pass null explicitly when needed
      let finalRoomId: string | null | undefined;
      if (shouldClearRoom) {
        finalRoomId = null;
      } else if (roomId === null) {
        finalRoomId = null; // Explicitly set to null for "No Room"
      } else {
        finalRoomId = roomId; // Keep existing or set new room
      }
      
      // Send the update to the server
      await api.patch(
        `/api/appointments/${appointmentId}/status`,
        {
          status: newStatus,
          room_id: finalRoomId,
          user_id: user?.id,
          needs_return_to_crc: needsReturn
        }
      );
      
      // Close any open dropdowns immediately
      setShowRoomDropdown(null);
      setShowStatusMenu(null);
      setShowLocationMenu(null);
      
      // Fetch all appointments to maintain order
      fetchAppointments();
      
    } catch (error: any) {
      console.error('Error updating appointment:', error);
      
      // Check if it's a specific error about unsupported status
      if (error.response?.status === 400) {
        alert(`This status change is not currently supported. Please contact your administrator to enable "${newStatus}" status.`);
      } else {
        alert('Error updating patient status. Please try again.');
      }
      
      // On error, fetch to restore correct state
      fetchAppointments();
    }
  };

  const updateInvestigator = async (appointmentId: string, investigatorName: string) => {
    try {
      // Update local state immediately for responsive UI
      setAppointments(prevAppointments => 
        prevAppointments.map(apt => 
          apt.id === appointmentId 
            ? { ...apt, investigator_name: investigatorName }
            : apt
        )
      );

      // Send update to server
      await api.patch(
        `/api/appointments/${appointmentId}`,
        {
          investigator_name: investigatorName
        }
      );
      
      // Don't call fetchAppointments here - let the local state update stand
    } catch (error) {
      console.error('Error updating investigator:', error);
      alert('Error updating investigator. Please try again.');
      // On error, fetch to restore correct state
      fetchAppointments();
    }
  };

  const getStatusColor = (appointment: Appointment): string => {
    const criticalStatuses = ['lobby', 'in_room', 'waiting_on_inv'];
    
    if (!criticalStatuses.includes(appointment.patient_status)) {
      return '';
    }

    const startTime = appointment.current_status_start || appointment.check_in_time;
    if (!startTime) return '';

    const minutesInStatus = differenceInMinutes(new Date(), new Date(startTime));

    if (minutesInStatus >= colorThresholds.red) return 'status-red';
    if (minutesInStatus >= colorThresholds.yellow) return 'status-yellow';
    if (minutesInStatus >= colorThresholds.green) return 'status-green';
    return '';
  };

  const getRowBackgroundClass = (status: string): string => {
    switch(status) {
      case 'not_arrived': return 'row-status-not-arrived';
      case 'lobby': return 'row-status-lobby';
      case 'in_room': return 'row-status-in-room';
      case 'with_crc': return 'row-status-with-crc';
      case 'waiting_on_inv': return 'row-status-waiting';
      case 'with_investigator': return 'row-status-with-inv';
      case 'done': return 'row-status-done';
      case 'no_show': return 'row-status-no-show';
      case 'cancelled': return 'row-status-cancelled';
      case 'rescheduled': return 'row-status-rescheduled';
      default: return '';
    }
  };

  const getVisitDuration = (appointment: Appointment): string => {
  // Check for manual override first (keep this for compatibility)
  if (appointment.manual_duration_override) {
    const hours = Math.floor(appointment.manual_duration_override / 60);
    const mins = appointment.manual_duration_override % 60;
    
    if (hours > 0) {
      return `${hours}h ${mins}m*`; // Asterisk shows manual override
    }
    return `${mins}m*`;
  }
  
  // SPECIAL CASE: For "done" status, show TOTAL visit duration
  if (appointment.patient_status === 'done') {
    // If we don't have check_in_time, can't calculate duration
    if (!appointment.check_in_time) return '—';
    
    // Use check_out_time if available, otherwise use current time (for backwards compatibility)
    const endTime = appointment.check_out_time 
      ? new Date(appointment.check_out_time) 
      : new Date();
    
    const startTime = new Date(appointment.check_in_time);
    const minutes = differenceInMinutes(endTime, startTime);
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    
    if (hours > 0) {
      return `${hours}h ${mins}m`;
    }
    return `${mins}m`;
  }
  
  // For all other statuses, show duration at CURRENT STATUS
  // Use current_status_start if available, otherwise use check_in_time for "lobby" status
  let startTime = appointment.current_status_start;
  
  // For lobby status, if no current_status_start, use check_in_time
  if (appointment.patient_status === 'lobby' && !startTime && appointment.check_in_time) {
    startTime = appointment.check_in_time;
  }
  
  // If still no start time, show dash
  if (!startTime) return '—';
  
  // Calculate minutes since current status started
  const minutes = differenceInMinutes(new Date(), new Date(startTime));
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  
  if (hours > 0) {
    return `${hours}h ${mins}m`;
  }
  return `${mins}m`;
};
  const getNextStatus = (currentStatus: string): { status: string; needsRoom?: boolean } => {
    const statusFlow: Record<string, { status: string; needsRoom?: boolean }> = {
      'not_arrived': { status: 'lobby' },
      'lobby': { status: 'in_room', needsRoom: true },
      'in_room': { status: 'with_crc' },
      'with_crc': { status: 'waiting_on_inv' },
      'waiting_on_inv': { status: 'with_investigator' },
      'with_investigator': { status: 'done' }
    };
    
    return statusFlow[currentStatus] || { status: 'done' };
  };

  const handleActionClick = (appointment: Appointment) => {
    const next = getNextStatus(appointment.patient_status);

    if (next.needsRoom && !appointment.room_id) {
      // Close other dropdowns and toggle room dropdown
      setShowStatusMenu(null);
      setShowLocationMenu(null);
      setShowRoomDropdown(showRoomDropdown === appointment.id ? null : appointment.id);
    } else {
      updateAppointmentStatus(appointment.id, next.status, appointment.room_id || undefined);
    }
  };

  const handleRoomSelect = (appointmentId: string, roomId: string) => {
    setShowRoomDropdown(null);
    updateAppointmentStatus(appointmentId, 'in_room', roomId);
  };

  const handleManualStatusChange = (appointmentId: string, newStatus: string) => {
    updateAppointmentStatus(appointmentId, newStatus);
    setShowStatusMenu(null);
  };

  const handleManualLocationChange = (appointmentId: string, roomId: string | null) => {
    const appointment = appointments.find(a => a.id === appointmentId);
    if (appointment) {
      setShowLocationMenu(null);
      // Determine appropriate status based on room assignment
      let newStatus = appointment.patient_status;
      if (roomId === null) {
        // No room selected → set to lobby
        newStatus = 'lobby';
      } else {
        // Room selected → set to in_room (Ready CRC)
        newStatus = 'in_room';
      }
      updateAppointmentStatus(appointmentId, newStatus, roomId === null ? null : roomId);
    }
  };

  const handleFilterToggle = (filterType: 'status' | 'study' | 'crc' | 'investigator', value: string) => {
    setFilters(prev => {
      const currentValues = [...prev[filterType]];
      const index = currentValues.indexOf(value);
      
      if (index > -1) {
        currentValues.splice(index, 1);
      } else {
        currentValues.push(value);
      }
      
      return {
        ...prev,
        [filterType]: currentValues
      };
    });
  };



  const clearFilter = (filterType: 'status' | 'study' | 'crc' | 'investigator') => {
    setFilters(prev => ({
      ...prev,
      [filterType]: []
    }));
  };

  const addManualAppointment = async () => {
    try {
      await api.post('/api/appointments', {
        ...newAppointment,
        appointment_date: new Date().toISOString().split('T')[0]
      });
      
      setShowAddModal(false);
      setNewAppointment({
        patient_initials: '',
        subject_id: '',
        study_name: '',
        visit_name: '',
        crc_name: '',
        appointment_time: format(new Date(), 'HH:mm')
      });
      fetchAppointments();
    } catch (error) {
      console.error('Error adding appointment:', error);
      alert('Error adding appointment. Please try again.');
    }
  };

  const handleEditClick = (appointment: Appointment) => {
    setEditingAppointment({
      ...appointment,
      appointment_time: appointment.appointment_time.substring(0, 5)
    });
    setShowEditModal(true);
  };

  const saveEditedAppointment = async () => {
    if (!editingAppointment) return;
    
    try {
      await api.patch(
        `/api/appointments/${editingAppointment.id}`,
        {
          patient_initials: editingAppointment.patient_initials,
          subject_id: editingAppointment.subject_id,
          study_name: editingAppointment.study_name,
          visit_name: editingAppointment.visit_name,
          crc_name: editingAppointment.crc_name,
          appointment_time: editingAppointment.appointment_time + ':00'
        }
      );
      
      setShowEditModal(false);
      setEditingAppointment(null);
      fetchAppointments();
    } catch (error) {
      console.error('Error updating appointment:', error);
      alert('Error updating appointment. Please try again.');
    }
  };

  const handleCRCCheckout = (appointmentId: string) => {
    if (window.confirm('Are you sure you want to check out this patient?')) {
      // Pass null explicitly to clear the room
      updateAppointmentStatus(appointmentId, 'done', null);
    }
  };

  const handleNotesClick = (appointment: Appointment) => {
    setEditingNotes({
      appointmentId: appointment.id,
      notes: appointment.notes || ''
    });
    setShowNotesModal(true);
  };

  const handleDurationClick = (appointment: Appointment) => {
  setSelectedAppointmentForDuration(appointment);
  setShowDurationModal(appointment.id);
};

  const saveNotes = async () => {
    try {
      await api.patch(
        `/api/appointments/${editingNotes.appointmentId}`,
        { notes: editingNotes.notes }
      );
      
      // Update local state
      setAppointments(prevAppointments => 
        prevAppointments.map(apt => 
          apt.id === editingNotes.appointmentId 
            ? { ...apt, notes: editingNotes.notes }
            : apt
        )
      );
      
      setShowNotesModal(false);
      setEditingNotes({ appointmentId: '', notes: '' });
    } catch (error) {
      console.error('Error saving notes:', error);
      alert('Error saving notes. Please try again.');
    }
  };

  const handleSignOut = async () => {
    await supabase.auth.signOut();
  };

  const toggleMobileRow = (appointmentId: string) => {
    setExpandedMobileRows(prev => {
      const newSet = new Set(prev);
      if (newSet.has(appointmentId)) {
        newSet.delete(appointmentId);
      } else {
        newSet.add(appointmentId);
      }
      return newSet;
    });
  };

  const getActionButtonText = (status: string): string => {
    const buttonText: Record<string, string> = {
      'not_arrived': 'Check In',
      'lobby': 'To Room',
      'in_room': 'CRC in Room',
      'with_crc': '',
      'waiting_on_inv': 'With INV',
      'with_investigator': 'Complete',
      'done': 'Checked Out'
    };
    return buttonText[status] || 'Update';
  };

  const getActionButtonClass = (status: string): string => {
    const buttonClass: Record<string, string> = {
      'not_arrived': 'btn-status-not-arrived',
      'lobby': 'btn-status-lobby',
      'in_room': 'btn-status-in-room',
      'with_crc': 'btn-status-with-crc',
      'waiting_on_inv': 'btn-status-waiting',
      'with_investigator': 'btn-status-with-inv',
      'done': 'btn-status-done'
    };
    return buttonClass[status] || '';
  };

  const getStatusClass = (status: string): string => {
    const statusClass: Record<string, string> = {
      'not_arrived': 'status-not-arrived',
      'lobby': 'status-lobby',
      'in_room': 'status-in-room',
      'with_crc': 'status-with-crc',
      'waiting_on_inv': 'status-waiting',
      'with_investigator': 'status-with-inv',
      'done': 'status-done',
      'no_show': 'status-no-show',
      'cancelled': 'status-cancelled',
      'rescheduled': 'status-rescheduled'
    };
    return statusClass[status] || '';
  };

  const getStatusIcon = (status: string) => {
    const icons: Record<string, JSX.Element> = {
      'not_arrived': <Clock size={14} />,
      'lobby': <Home size={14} />,
      'in_room': <MapPin size={14} />,
      'with_crc': <Users size={14} />,
      'waiting_on_inv': <AlertCircle size={14} />,
      'with_investigator': <Stethoscope size={14} />,
      'done': <Check size={14} />,
      'no_show': <X size={14} />,
      'cancelled': <XCircle size={14} />,
      'rescheduled': <Clock size={14} />
    };
    return icons[status] || <Activity size={14} />;
  };

  const formatStatus = (status: string): string => {
    const statusLabels: Record<string, string> = {
      'not_arrived': 'Not Arrived',
      'lobby': 'Lobby',
      'in_room': 'Ready CRC',
      'with_crc': 'w/CRC',
      'waiting_on_inv': 'Waiting on INV',
      'with_investigator': 'w/Investigator',
      'done': 'Done',
      'no_show': 'No Show',
      'cancelled': 'Cancelled',
      'rescheduled': 'Rescheduled'
    };
    return statusLabels[status] || status;
  };

  const getRoomDisplay = (appointment: Appointment): string => {
    if (appointment.rooms?.room_name) {
      return appointment.rooms.room_name;
    }
    if (appointment.room_id) {
      const room = rooms.find(r => r.id === appointment.room_id);
      return room ? room.room_name : '—';
    }
    return '—';
  };

  const filteredAppointments = appointments.filter(apt => {
    // Hide checked out filter
    if (filters.hideCheckedOut && apt.patient_status === 'done') return false;
    
    // Show only onsite filter - FIXED: now excludes no_show, cancelled, and rescheduled
    if (filters.showOnlyOnsite) {
      const excludedStatuses = ['not_arrived', 'done', 'no_show', 'cancelled', 'rescheduled'];
      if (excludedStatuses.includes(apt.patient_status)) return false;
    }
    
    // Status filter
    if (filters.status.length > 0 && !filters.status.includes(apt.patient_status)) return false;
    
    // Study filter
    if (filters.study.length > 0 && !filters.study.includes(apt.study_name)) return false;
    
    // CRC filter
    if (filters.crc.length > 0 && !filters.crc.includes(apt.crc_name)) return false;
    
    // Investigator filter
    if (filters.investigator.length > 0 && (!apt.investigator_name || !filters.investigator.includes(apt.investigator_name))) return false;
    
    return true;
  });

  const uniqueStudies = [...new Set(appointments.map((a: Appointment) => a.study_name))].filter(Boolean);
  const uniqueCRCs = [...new Set(appointments.map((a: Appointment) => a.crc_name))].filter(Boolean);

  // Calculate open rooms (rooms not currently occupied by an onsite patient)
  const occupiedRoomIds = new Set(
    appointments
      .filter(apt => {
        // Patient is onsite if not in these statuses
        const offSiteStatuses = ['not_arrived', 'done', 'no_show', 'cancelled', 'rescheduled'];
        return apt.room_id && !offSiteStatuses.includes(apt.patient_status);
      })
      .map(apt => apt.room_id)
  );

  const openRooms = rooms.filter(room => room.is_active && !occupiedRoomIds.has(room.id));

  // Show loading while checking entitlement
  if (checkingEntitlement) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  // Show Access Denied if user doesn't have Status Board entitlement
  if (entitlementChecked && !hasEntitlement && user?.role !== 'super_admin') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="text-center p-8 max-w-md">
          <div className="text-6xl mb-4">🔒</div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">Access Denied</h1>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            You do not have access to the Status Board for your current site.
            Please contact your administrator if you believe this is an error.
          </p>
          <button
            onClick={() => navigate('/')}
            className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
          >
            Return to Tool Hub
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-compact">
      <header className={`dashboard-header-compact ${isMobile && headerHidden ? 'header-hidden' : ''}`}>
        <div className="header-left">
          <div className="header-logo" onClick={() => navigate('/')}>
            <img src="/pilllogo.svg" alt="SiteOperations.io" className="logo-image" />
            <span className="logo-text">SiteOperations.io</span>
          </div>
          <span className="header-divider">|</span>
          <h1>Participant Status Board</h1>
        </div>

        <div className="header-right">
          <SiteSelector onSiteChange={() => {
            // Data will refresh via useEffect when currentSite changes
          }} />
          <span className="user-info">
            <User size={14} />
            <span className="user-details">
              <span className="user-name">{user?.full_name}</span>
              <span className="user-email">{user?.email}</span>
            </span>
          </span>

          <NotificationToggle />

          {isAdmin && (
            <button
              onClick={() => navigate('/status-board/admin')}
              className="btn-header"
            >
              <Settings size={14} />
              <span className="btn-text">Admin</span>
            </button>
          )}

          <ThemeToggle variant="onColor" />

          <button onClick={handleSignOut} className="btn-header">
            <LogOut size={14} />
            <span className="btn-text">Sign Out</span>
          </button>
        </div>
      </header>

      <div className="filters-section-compact">
         {isMobile && (
          <button 
            className="mobile-filter-toggle"
            onClick={() => setShowMobileFilters(!showMobileFilters)}
          >
            <Activity size={16} />
            Filters {showMobileFilters ? '▼' : '▶'}
          </button>
        )}
        
        <div className={`filters ${isMobile && showMobileFilters ? 'mobile-expanded' : ''}`}>

          <div className="filter-dropdown-container status">
            <button 
              className="filter-select-btn compact"
              onClick={() => setShowStatusDropdown(!showStatusDropdown)}
            >
              <span>
                {filters.status.length === 0 
                  ? 'All Status' 
                  : filters.status.length === 1
                  ? statusOptions.find(s => s.value === filters.status[0])?.label
                  : `${filters.status.length} selected`}
              </span>
              <ChevronRight size={12} className={showStatusDropdown ? 'rotated' : ''} />
            </button>
            {showStatusDropdown && (
              <div className="filter-dropdown">
                <div className="filter-dropdown-header">
                  <button onClick={() => clearFilter('status')} className="clear-btn">
                    Clear All
                  </button>
                </div>
                {statusOptions.map(option => (
                  <label key={option.value} className="filter-option">
                    <input
                      type="checkbox"
                      checked={filters.status.includes(option.value)}
                      onChange={() => handleFilterToggle('status', option.value)}
                    />
                    <span>{option.label}</span>
                  </label>
                ))}
              </div>
            )}
          </div>

          <div className="filter-dropdown-container study">
            <button 
              className="filter-select-btn compact"
              onClick={() => setShowStudyDropdown(!showStudyDropdown)}
            >
              <span>
                {filters.study.length === 0 
                  ? 'All Studies' 
                  : filters.study.length === 1
                  ? filters.study[0]
                  : `${filters.study.length} selected`}
              </span>
              <ChevronRight size={12} className={showStudyDropdown ? 'rotated' : ''} />
            </button>
            {showStudyDropdown && (
              <div className="filter-dropdown">
                <div className="filter-dropdown-header">
                  <button onClick={() => clearFilter('study')} className="clear-btn">
                    Clear All
                  </button>
                </div>
                {uniqueStudies.map(study => (
                  <label key={study} className="filter-option">
                    <input
                      type="checkbox"
                      checked={filters.study.includes(study)}
                      onChange={() => handleFilterToggle('study', study)}
                    />
                    <span>{study}</span>
                  </label>
                ))}
              </div>
            )}
          </div>

          <div className="filter-dropdown-container crc">
            <button 
              className="filter-select-btn compact"
              onClick={() => setShowCRCDropdown(!showCRCDropdown)}
            >
              <span>
                {filters.crc.length === 0 
                  ? 'All CRCs' 
                  : filters.crc.length === 1
                  ? filters.crc[0]
                  : `${filters.crc.length} selected`}
              </span>
              <ChevronRight size={12} className={showCRCDropdown ? 'rotated' : ''} />
            </button>
            {showCRCDropdown && (
              <div className="filter-dropdown">
                <div className="filter-dropdown-header">
                  <button onClick={() => clearFilter('crc')} className="clear-btn">
                    Clear All
                  </button>
                </div>
                {uniqueCRCs.map(crc => (
                  <label key={crc} className="filter-option">
                    <input
                      type="checkbox"
                      checked={filters.crc.includes(crc)}
                      onChange={() => handleFilterToggle('crc', crc)}
                    />
                    <span>{crc}</span>
                  </label>
                ))}
              </div>
            )}
          </div>

          <div className="filter-dropdown-container investigator">
            <button 
              className="filter-select-btn compact"
              onClick={() => setShowInvestigatorDropdown(!showInvestigatorDropdown)}
            >
              <span>
                {filters.investigator.length === 0 
                  ? 'All Investigators' 
                  : filters.investigator.length === 1
                  ? filters.investigator[0]
                  : `${filters.investigator.length} selected`}
              </span>
              <ChevronRight size={12} className={showInvestigatorDropdown ? 'rotated' : ''} />
            </button>
            {showInvestigatorDropdown && (
              <div className="filter-dropdown">
                <div className="filter-dropdown-header">
                  <button onClick={() => clearFilter('investigator')} className="clear-btn">
                    Clear All
                  </button>
                </div>
                {investigators.map(inv => (
                  <label key={inv.id} className="filter-option">
                    <input
                      type="checkbox"
                      checked={filters.investigator.includes(inv.name)}
                      onChange={() => handleFilterToggle('investigator', inv.name)}
                    />
                    <span>{inv.name}</span>
                  </label>
                ))}
              </div>
            )}
          </div>

          <button 
            className={`filter-toggle compact ${filters.hideCheckedOut ? 'active' : ''}`}
            onClick={() => setFilters({...filters, hideCheckedOut: !filters.hideCheckedOut})}
          >
            {filters.hideCheckedOut ? <EyeOff size={12} /> : <Eye size={12} />}
            Done
          </button>

          <button
            className={`filter-toggle compact ${filters.showOnlyOnsite ? 'active' : ''}`}
            onClick={() => setFilters({...filters, showOnlyOnsite: !filters.showOnlyOnsite})}
          >
            <Users size={12} />
            Onsite
          </button>
        </div>

        <div className="open-rooms-display">
          <span className="open-rooms-label">Open Rooms:</span>
          <span className="open-rooms-list">
            {openRooms.length === 0 ? (
              <span className="no-rooms">None</span>
            ) : (
              openRooms.map((room, index) => (
                <span key={room.id} className="open-room-name">
                  {room.room_name}{index < openRooms.length - 1 ? ', ' : ''}
                </span>
              ))
            )}
          </span>
        </div>

        <button onClick={() => setShowAddModal(true)} className="btn-primary-compact">
          <Plus size={14} />
          Add
        </button>
      </div>

      <div className="timeline-container-compact">
        <div className="timeline-header-compact">
          <div className="col-time">Time</div>
          <div className="col-patient">Patient</div>
          <div className="col-study">Study/Visit</div>
          <div className="col-crc">CRC</div>
          <div className="col-status">Status</div>
          <div className="col-location">Room</div>
          <div className="col-duration">Duration</div>
          <div className="col-investigator">Investigator</div>
          <div className="col-action">Action</div>
          <div className="col-notes">Notes</div>
        </div>

        <div className="timeline-body-compact">
          {filteredAppointments.length === 0 ? (
            <div className="no-appointments">
              <AlertCircle size={20} />
              <p>No appointments to display</p>
            </div>
          ) : (
            filteredAppointments.map(appointment => (
              <div 
                key={appointment.id} 
                className={`timeline-row-compact ${getStatusColor(appointment)} ${getRowBackgroundClass(appointment.patient_status)} ${
                  appointment.patient_status === 'done' || appointment.patient_status === 'no_show' || appointment.patient_status === 'cancelled' || appointment.patient_status === 'rescheduled' ? 'completed' : ''
                } ${expandedMobileRows.has(appointment.id) ? 'mobile-expanded' : ''}`}
                onDoubleClick={() => handleEditClick(appointment)}
                onPointerUp={(e) => {
                  // Only toggle if clicking the row itself, not buttons/interactive elements
                  const target = e.target as HTMLElement;
                  // Check for any interactive element
                  const isInteractive = target.closest(
                    'button, [role="button"], .clickable, .status-badge-compact, .location-text, .notes-preview, .investigator-btn, .action-btn-touch, select, input, textarea'
                  );
                  if (isMobile && !isInteractive) {
                    e.preventDefault();
                    toggleMobileRow(appointment.id);
                  }
                }}
                title={isMobile ? "Tap to expand" : "Double-click to edit"}
              >
                {isMobile && (
                  <span className="mobile-expand-indicator">›</span>
                )}
                <div className="col-time">
                  <Edit2 size={12} className="edit-icon" onClick={() => handleEditClick(appointment)} />
                  <span style={{ whiteSpace: 'nowrap' }}>
                    {format(new Date(`2000-01-01 ${appointment.appointment_time}`), 'h:mm a')}
                  </span>
                </div>
                <div className="col-patient">
                  <div className="patient-info-compact">
                    <span className="patient-primary">{appointment.patient_initials}</span>
                    {appointment.subject_id && (
                      <span className="patient-secondary">{appointment.subject_id}</span>
                    )}
                  </div>
                </div>
                <div className="col-study">
                  {isMobile && <span className="mobile-detail-label">Study/Visit</span>}
                  <div className="study-info-compact">
                    <span className="study-name">{appointment.study_name}</span>
                    <span className="visit-name">{appointment.visit_name}</span>
                  </div>
                </div>
                <div className="col-crc">
                  {appointment.crc_name}
                </div>
                <div className="col-status">
                  <div className="status-menu-container">
                    <span
                      className={`status-badge-compact ${getStatusClass(appointment.patient_status)} clickable`}
                      onPointerUp={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        
                        setShowStatusMenu(showStatusMenu === appointment.id ? null : appointment.id);
                        setShowLocationMenu(null);
                        setShowRoomDropdown(null);
                      }}
                    >
                      {getStatusIcon(appointment.patient_status)}
                      {formatStatus(appointment.patient_status)}
                    </span>
                    {/* Status menu rendered via portal on mobile - see bottom of component */}
                    {/* Desktop: render inline */}
                    {!isMobile && showStatusMenu === appointment.id && (
                      <div className="status-menu">
                        {statusOptions.map(option => (
                          <button
                            key={option.value}
                            className="status-option"
                            onClick={() => handleManualStatusChange(appointment.id, option.value)}
                          >
                            {option.label}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                <div className="col-location">
                  {isMobile && <span className="mobile-detail-label">Room</span>}
                  <div className="location-menu-container">
                    <span
                      className={`location-text clickable ${!appointment.room_id ? 'no-room' : ''}`}
                      onPointerUp={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        
                        setShowLocationMenu(showLocationMenu === appointment.id ? null : appointment.id);
                        setShowStatusMenu(null);
                        setShowRoomDropdown(null);
                      }}
                    >
                      {getRoomDisplay(appointment)}
                    </span>
                    {/* Location menu rendered via portal on mobile - see bottom of component */}
                    {/* Desktop: render inline */}
                    {!isMobile && showLocationMenu === appointment.id && (
                      <div
                        className="location-menu"
                        style={{ position: 'absolute', top: '100%', left: '0', marginTop: '4px' }}
                      >
                        <button
                          className="location-option"
                          onClick={() => handleManualLocationChange(appointment.id, null)}
                        >
                          No Room
                        </button>
                        {rooms.map(room => (
                          <button
                            key={room.id}
                            className="location-option"
                            onClick={() => handleManualLocationChange(appointment.id, room.id)}
                          >
                            {room.room_name}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                <div 
                    className="col-duration clickable-duration"
                    onClick={(e) => {
                      if (!isMobile) {
                        e.stopPropagation();
                        handleDurationClick(appointment);
                      }
                    }}
                    title={isMobile ? "" : "Click to view details"}
                    style={{ cursor: isMobile ? 'default' : 'pointer' }}
                >
                    {isMobile && <span className="mobile-detail-label">Duration</span>}
                    {getVisitDuration(appointment)}
                </div>

                    
                <div className="col-investigator">
                 {isMobile && <span className="mobile-detail-label">Investigator</span>}
                  <div className="investigator-buttons">
                    {investigators.map(inv => (
                      <button
                        key={inv.id}
                        className={`investigator-btn ${appointment.investigator_name === inv.name ? 'selected' : ''}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          updateInvestigator(appointment.id, inv.name);
                        }}
                      >
                        {inv.name}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="col-action">
                  {(appointment.patient_status !== 'done' && 
                    appointment.patient_status !== 'no_show' && 
                    appointment.patient_status !== 'cancelled' &&
                    appointment.patient_status !== 'rescheduled') && (
                    appointment.patient_status === 'with_investigator' ? (
                      <div className="action-buttons-split-compact">
                        {appointment.needs_return_to_crc ? (
                          <button 
                            className="action-btn-touch btn-back-to-crc"
                            onClick={() => updateAppointmentStatus(appointment.id, 'in_room', undefined, false)}
                            style={{ width: '100%' }}
                          >
                            Back to CRC
                          </button>
                        ) : (
                          <button 
                            className="action-btn-touch btn-checkout"
                            onClick={() => updateAppointmentStatus(appointment.id, 'done', null)}
                            style={{ width: '100%' }}
                          >
                            Check Out
                          </button>
                        )}
                      </div>
                    ) : appointment.patient_status === 'with_crc' ? (
                      <div className="action-buttons-triple-compact">
                        <button 
                          className="action-btn-touch btn-status-waiting"
                          onClick={() => updateAppointmentStatus(appointment.id, 'waiting_on_inv', undefined, false)}
                          title="Send to investigator - CRC is done"
                        >
                          <span style={{ fontSize: '0.65rem' }}>Ready INV</span>
                          <span style={{ fontSize: '0.6rem' }}>(Done)</span>
                        </button>
                        <button 
                          className="action-btn-touch btn-status-waiting need-back"
                          onClick={() => updateAppointmentStatus(appointment.id, 'waiting_on_inv', undefined, true)}
                          title="Send to investigator - Need patient back"
                        >
                          <span style={{ fontSize: '0.65rem' }}>Ready INV</span>
                          <span style={{ fontSize: '0.6rem' }}>(Need Back)</span>
                        </button>
                        <button 
                          className="action-btn-touch btn-checkout"
                          onClick={() => handleCRCCheckout(appointment.id)}
                        >
                          <span style={{ fontSize: '0.7rem' }}>Check Out</span>
                        </button>
                      </div>
                    ) : (
                      <div className="room-dropdown-container">
                        <button
                          className={`action-btn-touch ${getActionButtonClass(appointment.patient_status)}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            
                            handleActionClick(appointment);
                          }}
                        >
                          {getActionButtonText(appointment.patient_status)}
                        </button>
                        {/* Desktop room dropdown */}
                        {!isMobile && showRoomDropdown === appointment.id && (
                          <div
                            className="room-dropdown"
                            onClick={(e) => e.stopPropagation()}
                            onTouchStart={(e) => e.stopPropagation()}
                          >
                            {rooms.map(room => (
                              <button
                                key={room.id}
                                className="room-option"
                                onPointerDown={(e) => {
                                  e.stopPropagation();
                                  e.preventDefault();
                                  handleRoomSelect(appointment.id, room.id);
                                }}
                              >
                                {room.room_name}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    )
                  )}
                </div>
                <div className="col-notes">
                 {isMobile && <span className="mobile-detail-label">Notes</span>}
                  <div 
                    className="notes-preview"
                    onClick={() => handleNotesClick(appointment)}
                    title={appointment.notes || 'Click to add notes'}
                  >
                    {appointment.notes ? (
                      <span className="notes-text">{appointment.notes}</span>
                    ) : (
                      <span className="notes-placeholder">Add note...</span>
                    )}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Add Patient Modal */}
      {showAddModal && (
        <div className="modal-overlay" onClick={() => setShowAddModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h2>Add Walk-in Patient</h2>
            <div className="form-group">
              <label>Patient Initials</label>
              <input
                type="text"
                value={newAppointment.patient_initials}
                onChange={(e) => setNewAppointment({...newAppointment, patient_initials: e.target.value})}
                placeholder="e.g., JD"
              />
            </div>
            <div className="form-group">
              <label>Subject ID</label>
              <input
                type="text"
                value={newAppointment.subject_id}
                onChange={(e) => setNewAppointment({...newAppointment, subject_id: e.target.value})}
                placeholder="e.g., 12345"
              />
            </div>
            <div className="form-group">
              <label>Study Name</label>
              <input
                type="text"
                value={newAppointment.study_name}
                onChange={(e) => setNewAppointment({...newAppointment, study_name: e.target.value})}
                placeholder="e.g., COVID-VAX"
              />
            </div>
            <div className="form-group">
              <label>Visit Name</label>
              <input
                type="text"
                value={newAppointment.visit_name}
                onChange={(e) => setNewAppointment({...newAppointment, visit_name: e.target.value})}
                placeholder="e.g., Screening"
              />
            </div>
            <div className="form-group">
              <label>CRC Name</label>
              <input
                type="text"
                value={newAppointment.crc_name}
                onChange={(e) => setNewAppointment({...newAppointment, crc_name: e.target.value})}
                placeholder="e.g., Sarah"
              />
            </div>
            <div className="form-group">
              <label>Appointment Time</label>
              <input
                type="time"
                value={newAppointment.appointment_time}
                onChange={(e) => setNewAppointment({...newAppointment, appointment_time: e.target.value})}
              />
            </div>
            <div className="modal-actions">
              <button onClick={() => setShowAddModal(false)} className="btn-secondary">
                Cancel
              </button>
              <button 
                onClick={addManualAppointment} 
                className="btn-primary"
                disabled={!newAppointment.patient_initials || !newAppointment.study_name}
              >
                Add Patient
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Patient Modal */}
      {showEditModal && editingAppointment && (
        <div className="modal-overlay" onClick={() => setShowEditModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h2>Edit Appointment</h2>
            <div className="form-group">
              <label>Patient Initials</label>
              <input
                type="text"
                value={editingAppointment.patient_initials}
                onChange={(e) => setEditingAppointment({...editingAppointment, patient_initials: e.target.value})}
                placeholder="e.g., JD"
              />
            </div>
            <div className="form-group">
              <label>Subject ID</label>
              <input
                type="text"
                value={editingAppointment.subject_id || ''}
                onChange={(e) => setEditingAppointment({...editingAppointment, subject_id: e.target.value})}
                placeholder="e.g., 12345"
              />
            </div>
            <div className="form-group">
              <label>Study Name</label>
              <input
                type="text"
                value={editingAppointment.study_name}
                onChange={(e) => setEditingAppointment({...editingAppointment, study_name: e.target.value})}
                placeholder="e.g., COVID-VAX"
              />
            </div>
            <div className="form-group">
              <label>Visit Name</label>
              <input
                type="text"
                value={editingAppointment.visit_name}
                onChange={(e) => setEditingAppointment({...editingAppointment, visit_name: e.target.value})}
                placeholder="e.g., Screening"
              />
            </div>
            <div className="form-group">
              <label>CRC Name</label>
              <input
                type="text"
                value={editingAppointment.crc_name}
                onChange={(e) => setEditingAppointment({...editingAppointment, crc_name: e.target.value})}
                placeholder="e.g., Sarah"
              />
            </div>
            <div className="form-group">
              <label>Appointment Time</label>
              <input
                type="time"
                value={editingAppointment.appointment_time}
                onChange={(e) => setEditingAppointment({...editingAppointment, appointment_time: e.target.value})}
              />
            </div>
            <div className="modal-actions">
              <button onClick={() => setShowEditModal(false)} className="btn-secondary">
                Cancel
              </button>
              <button 
                onClick={saveEditedAppointment} 
                className="btn-primary"
                disabled={!editingAppointment.patient_initials || !editingAppointment.study_name}
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Notes Modal */}
      {showNotesModal && (
        <div className="modal-overlay" onClick={() => setShowNotesModal(false)}>
          <div className="modal notes-modal" onClick={(e) => e.stopPropagation()}>
            <h2>Patient Notes</h2>
            <div className="form-group">
              <label>Notes</label>
              <textarea
                className="notes-textarea"
                value={editingNotes.notes}
                onChange={(e) => setEditingNotes({...editingNotes, notes: e.target.value})}
                placeholder="Enter any notes about this patient visit..."
                rows={8}
              />
            </div>
            <div className="modal-actions">
              <button onClick={() => setShowNotesModal(false)} className="btn-secondary">
                Cancel
              </button>
              <button onClick={saveNotes} className="btn-primary">
                Save Notes
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Duration Modal */}
{showDurationModal && selectedAppointmentForDuration && (
  <DurationModal
    appointmentId={showDurationModal}
    patientName={`${selectedAppointmentForDuration.patient_initials} - ${selectedAppointmentForDuration.visit_name}`}
    onClose={() => {
      setShowDurationModal(null);
      setSelectedAppointmentForDuration(null);
    }}
    onUpdate={fetchAppointments}
  />
)}

      {/* Mobile backdrop for bottom sheet dropdowns */}
      {isMobile && (showStatusMenu || showLocationMenu || showRoomDropdown) && (
        <div
          className="mobile-backdrop"
          onClick={() => {
            setShowStatusMenu(null);
            setShowLocationMenu(null);
            setShowRoomDropdown(null);
          }}
        />
      )}

      {/* Status menu rendered via portal for mobile to escape stacking context */}
      {isMobile && showStatusMenu && createPortal(
        <div
          className="status-menu-portal"
          style={{
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            maxHeight: '60vh',
            background: 'white',
            borderRadius: '16px 16px 0 0',
            boxShadow: '0 -4px 20px rgba(0,0,0,0.3)',
            zIndex: 100010,
            padding: '8px 0 20px 0',
            overflowY: 'auto'
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <div style={{
            padding: '16px 20px',
            fontSize: '16px',
            fontWeight: 600,
            borderBottom: '1px solid var(--so-border)',
            textAlign: 'center'
          }}>
            Change Status
          </div>
          {statusOptions.map(option => (
            <button
              key={option.value}
              style={{
                display: 'block',
                width: '100%',
                padding: '16px 20px',
                border: 'none',
                background: 'white',
                textAlign: 'left',
                fontSize: '16px',
                minHeight: '52px',
                borderBottom: '1px solid var(--so-border)',
                cursor: 'pointer'
              }}
              onPointerDown={(e) => {
                e.stopPropagation();
                e.preventDefault();
                
                handleManualStatusChange(showStatusMenu, option.value);
              }}
              onTouchEnd={(e) => {
                e.stopPropagation();
                e.preventDefault();
                
                handleManualStatusChange(showStatusMenu, option.value);
              }}
            >
              {option.label}
            </button>
          ))}
        </div>,
        document.body
      )}

      {/* Location menu rendered via portal for mobile to escape stacking context */}
      {isMobile && showLocationMenu && createPortal(
        <div
          className="location-menu-portal"
          style={{
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            maxHeight: '60vh',
            background: 'white',
            borderRadius: '16px 16px 0 0',
            boxShadow: '0 -4px 20px rgba(0,0,0,0.3)',
            zIndex: 100010,
            padding: '8px 0 20px 0',
            overflowY: 'auto'
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <div style={{
            padding: '16px 20px',
            fontSize: '16px',
            fontWeight: 600,
            borderBottom: '1px solid var(--so-border)',
            textAlign: 'center'
          }}>
            Change Room
          </div>
          <button
            style={{
              display: 'block',
              width: '100%',
              padding: '16px 20px',
              border: 'none',
              background: 'white',
              textAlign: 'left',
              fontSize: '16px',
              minHeight: '52px',
              borderBottom: '1px solid var(--so-border)',
              cursor: 'pointer'
            }}
            onPointerDown={(e) => {
              e.stopPropagation();
              e.preventDefault();
              handleManualLocationChange(showLocationMenu, null);
            }}
            onTouchEnd={(e) => {
              e.stopPropagation();
              e.preventDefault();
              handleManualLocationChange(showLocationMenu, null);
            }}
          >
            No Room
          </button>
          {rooms.map(room => (
            <button
              key={room.id}
              style={{
                display: 'block',
                width: '100%',
                padding: '16px 20px',
                border: 'none',
                background: 'white',
                textAlign: 'left',
                fontSize: '16px',
                minHeight: '52px',
                borderBottom: '1px solid var(--so-border)',
                cursor: 'pointer'
              }}
              onPointerDown={(e) => {
                e.stopPropagation();
                e.preventDefault();
                
                handleManualLocationChange(showLocationMenu, room.id);
              }}
              onTouchEnd={(e) => {
                e.stopPropagation();
                e.preventDefault();
                
                handleManualLocationChange(showLocationMenu, room.id);
              }}
            >
              {room.room_name}
            </button>
          ))}
        </div>,
        document.body
      )}

      {/* Room dropdown rendered via portal for mobile to escape stacking context */}
      {isMobile && showRoomDropdown && createPortal(
        <div
          className="room-dropdown-portal"
          style={{
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            maxHeight: '60vh',
            background: 'white',
            borderRadius: '16px 16px 0 0',
            boxShadow: '0 -4px 20px rgba(0,0,0,0.3)',
            zIndex: 100010,
            padding: '8px 0 20px 0',
            overflowY: 'auto'
          }}
          onClick={(e) => {
            e.stopPropagation();
          }}
        >
          <div style={{
            padding: '16px 20px',
            fontSize: '16px',
            fontWeight: 600,
            borderBottom: '1px solid var(--so-border)',
            textAlign: 'center'
          }}>
            Select Room
          </div>
          {rooms.map(room => (
            <button
              key={room.id}
              style={{
                display: 'block',
                width: '100%',
                padding: '16px 20px',
                border: 'none',
                background: 'white',
                textAlign: 'left',
                fontSize: '16px',
                minHeight: '52px',
                borderBottom: '1px solid var(--so-border)',
                cursor: 'pointer'
              }}
              onPointerDown={(e) => {
                e.stopPropagation();
                e.preventDefault();
                
                handleRoomSelect(showRoomDropdown, room.id);
              }}
              onTouchEnd={(e) => {
                e.stopPropagation();
                e.preventDefault();
                
                handleRoomSelect(showRoomDropdown, room.id);
              }}
            >
              {room.room_name}
            </button>
          ))}
        </div>,
        document.body
      )}

    </div>
  );
};

export default Dashboard;